import { App, Form } from 'antd';
import type { SearchProps } from 'antd/es/input/Search';
import { useEffect } from 'react';
import Input from '../../../../Input';
import { useForm } from '../../../context';
import { cepMask } from '../../../helper';
import getCEP from '../api';

const brazilianStates = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

interface IProps {
  name?: string[];
}

const NationalAddress = ({ name }: IProps) => {
  name = name ?? [];

  const { form } = useForm();
  const { message } = App.useApp();

  const cep = Form.useWatch([...name, 'postalCode'], form);

  const onSearch: SearchProps['onSearch'] = async (value) => {
    if (value === '') {
      return;
    }

    try {
      const response = await getCEP.get(`${value}/json`);

      if (response.data.erro) {
        message.error('CEP não encontrado');
        return;
      }

      const fieldName = [...(name as string[])];
      form.setFieldValue([...fieldName, 'state'], response.data?.uf ?? '');
      form.setFieldValue([...fieldName, 'street'], response.data?.logradouro ?? '');
      form.setFieldValue([...fieldName, 'complement'], response.data?.complemento ?? '');
      form.setFieldValue([...fieldName, 'neighborhood'], response.data?.bairro ?? '');
      form.setFieldValue([...fieldName, 'city'], response.data?.localidade ?? '');
    } catch (error) {
      message.error('CEP não encontrado');
    }

    return;
  };

  useEffect(() => {
    if (cep?.length != 9) {
      return;
    }

    onSearch(cep);
  }, [cep]);

  return (
    <>
      <div className='grid grid-cols-1 sm:grid-cols-2 sm:gap-4'>
        <Form.Item
          label="CEP"
          name={[...name, 'postalCode']}
          rules={[
            {
              required: true,
              message: 'Por favor, digite o seu CEP!',
            },
          ]}
        >
          <Input.Search
            onChange={(e) => {
              const fixed = cepMask.apply(e.target.value);
              form.setFieldValue([...(name as string[]), 'postalCode'], fixed);
            }}
            placeholder="Digite seu CEP"
            onSearch={onSearch}
          />
        </Form.Item>

        <Form.Item
          label="Estado"
          name={[...name, 'state']}
          rules={[
            {
              required: true,
              message: 'Por favor, selecione o seu estado!',
            },
          ]}
        >
          <Input.Select
            showSearch
            placeholder="Selecione seu estado"
            optionFilterProp="label"
            options={brazilianStates}
          />
        </Form.Item>
      </div>

      <Form.Item
        label="Endereço"
        name={[...name, 'street']}
        rules={[
          {
            required: true,
            message: 'Por favor, digite seu endereço!',
          },
        ]}
      >
        <Input placeholder="Digite seu endereço" />
      </Form.Item>

      <div className='grid grid-cols-1 sm:grid-cols-3 sm:gap-4'>
        <div className='col-span-1'>
          <Form.Item
            label="Número"
            name={[...name, 'number']}
            rules={[{ required: true, message: 'Por favor, digite o número do endereço!' }]}
          >
            <Input placeholder="Digite o número" />
          </Form.Item>
        </div>

        <div className='col-span-2'>
          <Form.Item label="Complemento (opcional)" name={[...name, 'complement']}>
            <Input placeholder="Digite o complemento" />
          </Form.Item>
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 sm:gap-4'>
        <Form.Item
          label="Bairro"
          name={[...name, 'neighborhood']}
          rules={[{ required: true, message: 'Por favor, digite o bairro!' }]}
        >
          <Input placeholder="Digite o bairro" />
        </Form.Item>

        <Form.Item
          label="Cidade"
          name={[...name, 'city']}
          rules={[{ required: true, message: 'Por favor, digite a cidade!' }]}
        >
          <Input placeholder="Digite a cidade" />
        </Form.Item>
      </div>
    </>
  );
};

export default NationalAddress;
